import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { push } from 'connected-react-router';
import debounce from 'lodash/debounce';
import moment from 'moment-timezone';

import { PageHeader } from '../../../components/PageHeader';
import { Button, HEADER_BUTTON_DARK_BLUE } from '../../../components/PageHeader/Button';
import { DATE_TIME_SPACE_12_UPPERCASE } from '../../../constants';
import { TextInput } from '../../../components/PageHeader/TextInput';
import AdvancedFilters, { Filter } from '../../../components/AdvancedFilters/AdvancedFilters';
import AdvancedFiltersBar from '../../../components/AdvancedFilters/AdvancedFiltersBar';
import Table3, { columnHelper } from '../../../containers/Table/Table3';
import { ActionWidget, ActionButton } from '../../../components/ActionWidget';
import { notificationActions } from '../../../components/Notification/redux/actions';
import { openModalAction } from '../../../actions/modal';
import actions from './redux/actions';
import {
  GET_SUPERUSER_HEALTH_ALERT_RULES,
  DELETE_SUPERUSER_HEALTH_ALERT_RULE,
} from './redux/constants';
import {
  DETECTOR_KEYS,
  EDIT_ALERT_RULE_MODAL_NAME,
  SEVERITY_LEVELS,
  WINDOW_PRESET_OPTIONS,
} from '../../../modals/EditRuleModal';
import Strings from '../../../Strings';
import { showStatus } from '../../../utils';
import { buildCheckboxFilterDefinition, getAdvancedFilterValues } from './shared';
import { filterAlertRules, getAlertRuleCondition } from './alertRulesUtils';

const ALERT_RULE_FILTER_GROUPS = {
  detectorKey: 'healthAlertRuleDetectorKey',
  severity: 'healthAlertRuleSeverity',
  windowPreset: 'healthAlertRuleWindowPreset',
  status: 'healthAlertRuleStatus',
};

const STATUS_OPTIONS = [
  { value: 'true', label: 'Active' },
  { value: 'false', label: 'Inactive' },
];

const ALERT_RULE_FILTER_DEFINITIONS = {
  ...buildCheckboxFilterDefinition(ALERT_RULE_FILTER_GROUPS.detectorKey, 'Detector Key', DETECTOR_KEYS),
  ...buildCheckboxFilterDefinition(ALERT_RULE_FILTER_GROUPS.severity, 'Severity', SEVERITY_LEVELS),
  ...buildCheckboxFilterDefinition(ALERT_RULE_FILTER_GROUPS.windowPreset, 'Time Window', WINDOW_PRESET_OPTIONS),
  ...buildCheckboxFilterDefinition(ALERT_RULE_FILTER_GROUPS.status, 'Status', STATUS_OPTIONS),
};

function AlertRules(props) {
  const {
    getAlertRules,
    deleteAlertRule,
    filters,
    refreshTimestamp,
    showNotification,
    onNavigate,
    openEditRuleModal,
  } = props;
  
  const [data, setData] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  const tableRef = useRef(null);
  
  const pageRequest = useRef({});

  const fetchData = () => {
    setIsLoading(true);
    
    getAlertRules(pageRequest.current).then(resp => {
      if (resp.type === `${GET_SUPERUSER_HEALTH_ALERT_RULES}/result`) {
        setData(resp.response || []);
      }
      setIsLoading(false);
    }).catch(() => {
      setIsLoading(false);
    });
    
    tableRef?.current?.resetSelectedRows();
  };

  useEffect(() => {
    pageRequest.current.offset = 0;
    fetchData();
  }, [refreshTimestamp]);

  const filteredData = React.useMemo(() => filterAlertRules(data, {
    detectorKeys: getAdvancedFilterValues(filters, ALERT_RULE_FILTER_GROUPS.detectorKey),
    severities: getAdvancedFilterValues(filters, ALERT_RULE_FILTER_GROUPS.severity),
    windowPresets: getAdvancedFilterValues(filters, ALERT_RULE_FILTER_GROUPS.windowPreset),
    statuses: getAdvancedFilterValues(filters, ALERT_RULE_FILTER_GROUPS.status),
    searchQuery,
  }), [data, filters, searchQuery]);

  // Search
  const searchQueryDebounced = React.useMemo(
    () => debounce(query => setSearchQuery(query), 250),
    [],
  );

  useEffect(() => () => searchQueryDebounced.cancel(), [searchQueryDebounced]);

  const onSortingChange = sorting => {
    const sortColumn = sorting?.length > 0 ? sorting[0].id : undefined;
    const sortType = sorting?.length > 0 ? (sorting[0].desc ? 'descending' : 'ascending') : undefined;
    if (sortColumn === pageRequest.current.sortColumn && sortType === pageRequest.current.sortType) return;
    pageRequest.current.sortColumn = sortColumn;
    pageRequest.current.sortType = sortType;
    fetchData();
  };

  const onRowClick = row => {
    onNavigate(`/superuser-health/alerts/rules/${encodeURIComponent(row.id)}`);
  };

  // Actions
  const onDeleteRules = (rows) => {
    const toDelete = rows || selectedRows;
    toDelete.forEach(row => {
      deleteAlertRule(row.rule_id).then(resp => {
        if (resp.type !== DELETE_SUPERUSER_HEALTH_ALERT_RULE) {
          showNotification('Failed to delete alert rule', 5000, true);
        }
        fetchData();
      });
    });
  };

  // Columns
  const columns = React.useMemo(() => [
    columnHelper.accessor('id', {
      header: 'ID',
      cell: info => info.getValue() || '-',
      size: 120,
    }),
    columnHelper.accessor('description', {
      header: 'Description',
      cell: info => info.getValue() || '-',
      size: 300,
    }),
    columnHelper.accessor('detector_key', {
      header: 'Detector Key',
      cell: info => info.getValue() ? DETECTOR_KEYS.find(d => d.value === info.getValue())?.label : '',
      size: 150,
    }),
    columnHelper.accessor('window_preset', {
      header: 'Window Preset',
      accessorFn: row => row.detector_config?.window_preset,
      cell: info => info.getValue() ? WINDOW_PRESET_OPTIONS.find(w => w.value === info.getValue())?.label : '-',
      size: 150,
    }),
    columnHelper.accessor('fail_categories_include', {
      header: 'Included Fail Categories',
      accessorFn: row => row.detector_config?.fail_categories_include?.join(', '),
      cell: info => info.getValue() || '-',
      size: 250,
    }),
    columnHelper.accessor('fail_categories_exclude', {
      header: 'Excluded Fail Categories',
      accessorFn: row => row.detector_config?.fail_categories_exclude?.join(', '),
      cell: info => info.getValue() || '-',
      size: 250,
    }),
    columnHelper.accessor('cap_id_pattern', {
      header: 'CAP ID Pattern',
      accessorFn: row => row.detector_config?.cap_id_pattern || '',
      cell: info => info.getValue() || '-',
      size: 200,
    }),
    columnHelper.accessor('condition', {
      header: 'Condition',
      accessorFn: getAlertRuleCondition,
      cell: info => info.getValue(),
      size: 100,
      enableSorting: false,
    }),
    columnHelper.accessor('active', {
      header: 'Active',
      cell: info => showStatus(info.getValue()),
      size: 80,
    }),
    columnHelper.accessor('created_at', {
      header: 'Created',
      cell: info => moment(info.getValue()).format(DATE_TIME_SPACE_12_UPPERCASE),
      size: 180,
    }),
  ], []);

  // Render
  const pageHeaderLeft = isLoading 
    ? 'Loading...'
    : `${filteredData.length} Alert Rules`;

  const pageHeaderRight = (
    <>
      <TextInput 
        className="search" 
        placeholder="Search descriptions..."
        onChange={e => searchQueryDebounced(e.target.value)} 
      />
      <AdvancedFilters>
        <Filter definition={ALERT_RULE_FILTER_DEFINITIONS[ALERT_RULE_FILTER_GROUPS.detectorKey]} />
        <Filter definition={ALERT_RULE_FILTER_DEFINITIONS[ALERT_RULE_FILTER_GROUPS.severity]} />
        <Filter definition={ALERT_RULE_FILTER_DEFINITIONS[ALERT_RULE_FILTER_GROUPS.windowPreset]} />
        <Filter definition={ALERT_RULE_FILTER_DEFINITIONS[ALERT_RULE_FILTER_GROUPS.status]} />
      </AdvancedFilters>
      <Button class={HEADER_BUTTON_DARK_BLUE} onClick={() => openEditRuleModal({ rule: null, onSuccess: fetchData })} title="Add Rule" />
    </>
  );

  const buttons = [
    {
      icon: 'edit',
      onClick: (_, row) => openEditRuleModal({ rule: row, onSuccess: fetchData }),
      text: Strings.edit,
    },
    {
      icon: 'delete',
      onClick: (_, row) => onDeleteRules([row]),
      text: Strings.delete,
    },
  ];

  return (
    <div className="alert-rules-page">
      <PageHeader left={pageHeaderLeft} right={pageHeaderRight} />
      <AdvancedFiltersBar customFilters={ALERT_RULE_FILTER_DEFINITIONS} />
      <Table3
        isLoading={isLoading}
        name="alert-rules"
        data={filteredData}
        columns={columns}
        onSortingChange={onSortingChange}
        onRowClick={onRowClick}
        onRowSelectionChange={setSelectedRows}
        enableColumnFiltering
        buttons={buttons}
      />
      <ActionWidget show={selectedRows?.length > 0}>
        <ActionButton 
          img="action-delete" 
          tooltiptext="Delete Rules" 
          text="Delete" 
          action={onDeleteRules} 
        />
      </ActionWidget>
    </div>
  );
}

AlertRules.propTypes = {
  getAlertRules: PropTypes.func.isRequired,
  deleteAlertRule: PropTypes.func.isRequired,
  filters: PropTypes.array.isRequired,
  refreshTimestamp: PropTypes.any.isRequired,
  showNotification: PropTypes.func.isRequired,
  onNavigate: PropTypes.func.isRequired,
  openEditRuleModal: PropTypes.func.isRequired,
};

const mapStateToProps = state => ({
  filters: state.entities.advancedFilters.filters.items,
});

const mapDispatchToProps = dispatch => ({
  getAlertRules: pageRequest => dispatch(actions.getAlertRules(pageRequest)),
  deleteAlertRule: ruleId => dispatch(actions.deleteAlertRule(ruleId)),
  showNotification: (message, timeout, error) => dispatch(notificationActions.show(message, timeout, error)),
  onNavigate: path => dispatch(push(path)),
  openEditRuleModal: data => dispatch(openModalAction(EDIT_ALERT_RULE_MODAL_NAME, data)),
});

export default connect(mapStateToProps, mapDispatchToProps)(AlertRules);
